import { useEffect, useMemo, useRef } from 'react';
import { useIdleTimer } from 'react-idle-timer';

import {
  env,
  useDomainDetailInfoQuery,
  useIdleSessionStore,
  useServiceAppState,
} from '@kc-console/context';
import { getAccountBaseUrl, minutesToMilliseconds } from '@kc-console/core';

const CHANNEL_NAME = 'kc-session-timeout-timer';

const INITIAL_SESSION_EXPIRE_TIME_MINS = 30;
const RESET_INTERVAL_TIME = minutesToMilliseconds(5);

/**
 * 사용자의 유휴상태를 감지하고, 유휴상태라면 로그아웃 처리하는 하는 컴포넌트입니다.
 *
 * @description
 * - DISABLE_IDLE_BOUNDARY 환경변수가 'true'인 경우 idle 로직을 멈춥니다.
 * - 커스텀 세션 타임아웃 값이 있는 경우 해당 값을 사용하고, 그렇지 않으면 iam 값을 사용합니다.
 * - 세션이 만료되면 IAM 엔드포인트로 리다이렉트하여 로그아웃 처리합니다.
 * - 일정 간격으로 idle 로직을 재시작합니다.
 *
 */
const IdleBoundary = () => {
  const {
    state: { iamEndpoint },
  } = useServiceAppState();
  const { data } = useDomainDetailInfoQuery({});
  const { session_ttl_mins: sessionTtlMins } = data ?? {};

  const sessionTtlMinsFromIam = sessionTtlMins ?? INITIAL_SESSION_EXPIRE_TIME_MINS;
  const isPauseExpired = useIdleSessionStore(state => state.isPauseExpired);
  const sessionExpireTimeMins = useIdleSessionStore(state => state.sessionExpireTimeMins);

  const sessionTimeout = useMemo(() => {
    /**
     * 서비스에서 전달한 커스텀 session timeout 값이 있을 경우, 해당 값을 우선적으로 사용합니다.
     */
    if (!!sessionExpireTimeMins && sessionExpireTimeMins > 0) {
      return minutesToMilliseconds(sessionExpireTimeMins);
    }

    /**
     * iam domain info의 sessionTtlMins 또는 INITIAL_SESSION_EXPIRE_TIME_MINS (30분) 를 사용합니다.
     * state.auth.domainInfo.session_ttl_mins ?? INITIAL_SESSION_EXPIRE_TIME_MINS
     */
    return minutesToMilliseconds(sessionTtlMinsFromIam);
  }, [sessionExpireTimeMins, sessionTtlMinsFromIam]);

  const isIdlePass = env.DISABLE_IDLE_BOUNDARY === 'true';
  const intervalId = useRef<number>(0);
  const { reset, pause } = useIdleTimer({
    timeout: sessionTimeout,
    events: ['mousedown', 'mousemove'],
    onIdle: () => {
      const currentURL = encodeURIComponent(window.location.href);
      window.location.href = `${iamEndpoint}/auth/logout?redirect_uri=${getAccountBaseUrl(
        window.location.origin,
      )}/session-timeout?redirect_uri=${currentURL}`;
    },
    name: CHANNEL_NAME,
    crossTab: true,
  });

  /**
   * idle 로직을 사용하지 않는 환경에서는 idle 로직을 멈추도록 합니다.
   * idle 로직을 임의로 멈추도록 했다면 일정간격으로 idle 로직을 재시작합니다.
   * 그렇지 않다면 idle 로직을 재시작합니다.
   */
  useEffect(() => {
    if (isIdlePass) {
      pause();
      return;
    }
    if (isPauseExpired) {
      intervalId.current = window.setInterval(() => {
        reset();
      }, RESET_INTERVAL_TIME);
    } else {
      window.clearTimeout(intervalId.current);
      reset();
    }
  }, [isPauseExpired, reset, isIdlePass, pause]);
  return null;
};

export default IdleBoundary;
